"use client";

import { getStoredUtmSource } from "@/components/feature/utm-source-capture";
import { EmailCaptureModal } from "@/components/ui/email-capture-modal";
import { useIsGrownup } from "@/hooks/use-is-grownup";
import { DEFAULT_EMAIL_SOURCE } from "@/lib/constants/email-capture";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useCallback, useEffect, useMemo, useState } from "react";

const PRESENTED_KEY = "playmath.emailCaptureModalPresented";
const ELIGIBLE_PATHS = new Set<string>(["/about", "/partners"]);

function safeGetPresented(): boolean {
  try {
    return window.localStorage.getItem(PRESENTED_KEY) === "true";
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("EmailCaptureGate: localStorage unavailable (read)", err);
    }
    return false;
  }
}

function safeSetPresented(): void {
  try {
    window.localStorage.setItem(PRESENTED_KEY, "true");
  } catch (err) {
    if (process.env.NODE_ENV === "development") {
      // eslint-disable-next-line no-console
      console.warn("EmailCaptureGate: localStorage unavailable (write)", err);
    }
    // Non-critical: if storage is unavailable, we may show again on next click.
  }
}

function findAnchorFromEventTarget(target: EventTarget | null): HTMLAnchorElement | null {
  if (!(target instanceof Element)) return null;
  return target.closest("a") as HTMLAnchorElement | null;
}

function isPlainLeftClick(e: MouseEvent, anchor: HTMLAnchorElement): boolean {
  if (e.defaultPrevented) return false;
  if (e.button !== 0) return false;
  if (e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return false;
  if (anchor.target && anchor.target !== "_self") return false;
  return true;
}

function getGamesDestinationFromAnchor(anchor: HTMLAnchorElement): string | null {
  const href = anchor.getAttribute("href") || "";
  if (!href) return null;

  // Ignore non-http(s) schemes, javascript:, mailto:, etc.
  if (/^[a-zA-Z][a-zA-Z0-9+.-]*:/.test(href) && !href.startsWith("http")) {
    return null;
  }

  try {
    const url = new URL(href, window.location.origin);
    // Only intercept same-origin navigations.
    if (url.origin !== window.location.origin) return null;

    const path = url.pathname;
    const isGames = path === "/games" || path.startsWith("/games/");
    if (!isGames) return null;

    return `${url.pathname}${url.search}${url.hash}`;
  } catch {
    return null;
  }
}

export function EmailCaptureGate() {
  const pathname = usePathname();
  const router = useRouter();
  const searchParams = useSearchParams();
  const isGrownup = useIsGrownup();

  const source = useMemo(() => {
    const stored = getStoredUtmSource();
    if (stored) return stored;
    return searchParams.get("utm_source") ?? DEFAULT_EMAIL_SOURCE;
  }, [searchParams]);

  const [open, setOpen] = useState(false);
  const [destination, setDestination] = useState<string | null>(null);

  const handleSuccess = useCallback(() => {
    setOpen(false);
    if (destination) {
      router.push(destination);
    }
  }, [destination, router]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    if (!ELIGIBLE_PATHS.has(pathname)) return;

    const handler = (e: MouseEvent) => {
      if (!isGrownup) return;
      if (safeGetPresented()) return;

      const anchor = findAnchorFromEventTarget(e.target);
      if (!anchor) return;
      if (!isPlainLeftClick(e, anchor)) return;

      const next = getGamesDestinationFromAnchor(anchor);
      if (!next) return;

      e.preventDefault();

      safeSetPresented();
      setDestination(next);
      setOpen(true);
    };

    document.addEventListener("click", handler, { capture: true });
    return () => document.removeEventListener("click", handler, { capture: true });
  }, [pathname, isGrownup]);

  return (
    <EmailCaptureModal
      open={open}
      onOpenChange={setOpen}
      source={source}
      onSuccess={handleSuccess}
    />
  );
}
